
import React, { Component } from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import ReorderIcon from './ReorderIcon'

import * as actions from '../../../actionTypes'
import { FACTOR_IMPORTANCE } from '../../../constants'
import FormHeader from '../FormHeader'
import ActionModal from '../../common/ActionModal'

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
	const result = Array.from(list)
	const [removed] = result.splice(startIndex, 1)
	result.splice(endIndex, 0, removed)

	return result
}

const getItemStyle = (isDragging, draggableStyle) => ({
	// some basic styles to make the items look a bit nicer
	alignItems: 'center',
	background: '#EEE',
	borderRadius: '0.5rem',
	display: 'flex',
	userSelect: 'none',
	padding: '1rem',
	margin: `0 0 1rem 0`,
	...draggableStyle
})

const getListStyle = () => ({
	background: 'none',
	marginTop: '2rem',
	marginRight: 'auto',
	marginLeft: 'auto',
	padding: '0',
	width: '100%'
})

const getSpanStyle = () => ({
	marginLeft: '1.25rem'
})

const getSelectStyle = () => ({
	marginLeft: '1.25rem'
})

class OrderOfImportanceContainer extends Component {
  constructor(props) {
		super(props)
		this.state = {
			items: this.props.topThreeFactors,
			isWarningModalVisible: false,
			storedFn: null
		}
		this.initOrderOfImportance()
		this.onDragEnd = this.onDragEnd.bind(this)
		this.onSelectChange = this.onSelectChange.bind(this)
		this.onSelectFocus = this.onSelectFocus.bind(this)
		this.storedIndex = 0
	}

	initOrderOfImportance() {
		const { topThreeFactorsWithCompareWeights, topThreeFactors, setFactorsWithCompareWeights } = this.props

		if (!topThreeFactorsWithCompareWeights.length) {
			const factorsWithCompareWeights = topThreeFactors.map(text => (
				{ text, weight: FACTOR_IMPORTANCE.uninitializedWeight }
			))
			setFactorsWithCompareWeights(factorsWithCompareWeights)
		}
	}

	handleDragReOrder = (reorder, result) => {
		const {navigator} = this.props
		if (this.state.isWarningModalVisible) {
			this.setState({ isWarningModalVisible: false })
		}
		this.props.reorderFactorImportance(reorder, result)
		if(navigator.isForwardDisabled()) {
			navigator.enableForward()
		}
	}

	onDragEnd(result) {
		const { hasBeenRanked, topThreeFactorsWithCompareWeights } = this.props
		const hasWeights = topThreeFactorsWithCompareWeights.some(factor => factor.weight > 0)
		if (hasBeenRanked && hasWeights) { // Previously saved data throw modal
			this.setState({
				isWarningModalVisible: true,
				storedFn: () => this.handleDragReOrder(reorder, result)
			})
		} else { // No previous data continue
			this.handleDragReOrder(reorder, result)
		}
	}

	onSelectChange(e) {
		const {navigator} = this.props
		this.props.reorderFactorImportance(reorder, {source: { index: this.storedIndex}, destination: {index: e.target.value}})

		this.storedIndex = parseInt(e.target.value, 10)
		if(navigator.isForwardDisabled()) {
      navigator.enableForward()
    }
	}

	onSelectFocus(e) {
		this.storedIndex = parseInt(e.target.value, 10)
	}

  componentDidMount() {
		const { navigator, hasBeenRanked } = this.props
		if (!hasBeenRanked) {
			navigator.disableForward()
		} else {
			navigator.enableForward()
		}

	}

	renderWarningModal = () => {
		const modalContent = 'Order Reset Warning'
    const buttonText = 'Cancel'
		const {t} = this.props
    const buttons = [
			<Button onClick={() => this.state.storedFn()}>{t('Continue')}</Button>
		]
		const dismiss = () => this.setState({
			isWarningModalVisible: false,
			storedFn: null
		})

    return (
      <ActionModal
        content={t(modalContent)}
        dismissText={t(buttonText)}
				open={this.state.isWarningModalVisible}
				onDismiss={dismiss}
        buttons={buttons}
      />
    )
	}

  render() {
		const { t, navigator } = this.props

    return (
      <Grid
        alignContent="flex-start"
        className="counseling-session-page-container"
        container
        justify="center"
        spacing={8}
      >
        <FormHeader
          description={t('Order By Importance')}
          stepNumber={t(`Step ${navigator.getStepNumber()}`)}
					helpText={t('Order By Importance Help')}
        />
        <Grid item xs={10}>
					<DragDropContext onDragEnd={this.onDragEnd}>
						<Droppable droppableId='droppable'>
							{(provided, snapshot) => (
								<div
									{...provided.droppableProps}
									ref={provided.innerRef}
									style={getListStyle(snapshot.isDraggingOver)}
								>
									{this.props.topThreeFactors.map((factor, index) => (
										<Draggable
											key={factor + index}
											draggableId={factor}
											index={index}
											isDragDisabled={this.props.isCounselingSessionDisabled}
										>
											{(provided, snapshot) => (
												<div
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													style={getItemStyle(
														snapshot.isDragging,
														provided.draggableProps.style
													)}
												>
													<ReorderIcon />
													<select
														name='index'
														index={index}
														value={index}
														onChange={this.onSelectChange}
														onFocus={this.onSelectFocus}
														style={getSelectStyle()}
														disabled={this.props.isCounselingSessionDisabled}
													>
														<option value='0'>1</option>
														<option value='1'>2</option>
														<option value='2'>3</option>
													</select>
													<span style={getSpanStyle()}>
														{factor}
													</span>
												</div>
											)}
										</Draggable>
									))}
									{provided.placeholder}
								</div>
							)}
						</Droppable>
					</DragDropContext>
        </Grid>
      {this.renderWarningModal()}
			</Grid>
    )
  }
}

const mapStateToProps = state => ({
	topThreeFactors: state.counselingSession.counselingSessionData.topThreeFactors,
	topThreeFactorsWithCompareWeights: state.counselingSession.counselingSessionData.topThreeFactorsWithCompareWeights,
	stepNumberToDisplay: state.counselingSession.stepNumberToDisplay,
	hasBeenRanked: state.counselingSession.counselingSessionData.hasBeenRanked
})

const mapDispatchToProps = dispatch => ({
	setFactorsWithCompareWeights(factors) {
		const action = { type: actions.SET_ORDER_FACTOR_IMPORTANCE, payload: factors }
		dispatch(action)
	},
  resetCompareArray() {
    const action = {type: actions.RESET_COMPARE_ARRAY}
    dispatch(action)
  },
  resetFinalWeight() {
    const action = {type: actions.RESET_FINAL_WEIGHT}
    dispatch(action)
	},
	reorderFactorImportance(reorder, result) {
		const action = {type: actions.REORDER_FACTOR_IMPORTANCE, payload: {reorder, result}}
		dispatch(action)
	}
})

export default compose(
  translate(),
  connect(mapStateToProps, mapDispatchToProps),
)(OrderOfImportanceContainer)


// WEBPACK FOOTER //
// ./src/components/counseling-session/order-of-importance/OrderOfImportanceContainer.js